import { orderBy, set, sortBy } from 'lodash-es';
import type { HistoryState, RouteLocation, RouteLocationRaw } from 'vue-router';

import type { Cluster, Distribution } from '@/api-client/api';
import { useMainStore } from '@/stores/main';
import { useMultiClusterStore } from '@/stores/multiCluster';

import constants from '../constants';
import { error } from '../logger';
import router from '../router';

// Minimal interface for generic K8s resource functions that accept any resource type
// (Profiles, Policies, Clusters, etc.) — no equivalent exported in @/api-client/api
interface K8sResource {
  metadata?: {
    name?: string;
    namespace?: string;
    labels?: Record<string, string>;
  };
}

interface BootstrapCondition {
  type: string;
  status: string;
}

interface ClusterLabelOption {
  value: string;
  label: string;
  id: string;
}

// At runtime, router.push() and router.resolve() work with both RouteLocationRaw (raw route
// objects) and RouteLocation (normalized/resolved routes that callers sometimes pass directly,
// e.g. when re-navigating to a saved beforeEach destination). The union type reflects actual
// usage while remaining more precise than `any`.
type RouteInput = RouteLocationRaw | RouteLocation;

// router.resolve() returns RouteLocation & { href: string }; we extend it with the optional
// History API state that callers may pass through via params.state.
type ResolvedRoute = RouteLocation & { href: string; state?: HistoryState };

/**
 * Is this resource (Profile/Policy) a globally distributed resource.
 * @param res - resource object
 * @return true if resource has the distribution label
 */
export function isGlobalResource(res: K8sResource | undefined | null): boolean {
  if (!res?.metadata?.labels) return false;
  return Object.keys(res?.metadata?.labels).includes(constants.MULTICLUSTER_LABEL);
}

/**
 * Is this a multicluster namespaced object?
 */
export function isMultiClusterNamespaced(obj: K8sResource): boolean {
  return obj?.metadata?.namespace === constants.MULTICLUSTER_NAMESPACE;
}

/**
 * Search distributions in $store for the given resource.
 * @param resourceName
 * @return List of matching distribution names
 */
export function relatedDistributions(resourceName: string): string[] {
  const store = useMainStore();
  return store.distributions.reduce((distnNames: string[], dist: Distribution) => {
    if (dist.spec?.sources?.find((source) => source.ref?.name === resourceName)) {
      const name = dist.metadata?.name;
      if (name) distnNames.push(name);
    }
    return distnNames;
  }, []);
}

/**
 * Collect labels from all given clusters, dedup, sort, and return as tuples.
 * Example response:
 * [ [ 'cluster-type', 'primary' ], [ 'foo', 'bar' ] ]
 * @param clusters - array of cluster objects
 */
export function sortAndDedupClusterLabels(clusters: Cluster[]): [string, string][] {
  if (!clusters) {
    error('sortAndDedupClusterLabels() NULL clusters');
    return [];
  }
  const map = new Map<string, [string, string]>();
  for (const cl of clusters) {
    for (const [k, v] of Object.entries(cl.metadata?.labels ?? {})) {
      map.set(`${k}=${v}`, [k, v]);
    }
  }
  return sortBy(Array.from(map.values()));
}

/**
 * Calls Vue router.push function with cluster name
 * @param params - router params object
 */
export function routeTo(params: RouteInput): ReturnType<typeof router.push> {
  const multiClusterStore = useMultiClusterStore();
  if (
    multiClusterStore.proxiedCluster &&
    typeof params !== 'string' &&
    !(params as RouteLocation).query?.['cl']
  ) {
    set(params, 'query.cl', multiClusterStore.proxiedCluster);
  }
  return router.push(params as RouteLocationRaw);
}

// for providing route to link component
export function routeToResolvedGeneric(params: RouteInput): ResolvedRoute {
  const multiClusterStore = useMultiClusterStore();

  if (
    multiClusterStore.proxiedCluster &&
    typeof params !== 'string' &&
    !(params as RouteLocation).query?.['cl']
  ) {
    set(params, 'query.cl', multiClusterStore.proxiedCluster);
  }
  const resolved = router.resolve(params as RouteLocationRaw) as ResolvedRoute;

  // router.resolve returns a normalized route location (URL, path, params, query, matched components).
  // State is part of the History API, not the resolved route, so Vue Router omits it from the returned object.
  // If state was provided in params, add it back to the resolved object.
  const state = (params as RouteLocation & { state?: HistoryState }).state;
  if (state) {
    resolved.state = state;
  }
  return resolved;
}

/**
 * Fetch clusters and create entries in the cluster labels selector.
 * Used in DistributionForm and RoleBindingForm.
 * @return Array of Option objects
 */
export async function createClusterLabelOptionsForListbox(): Promise<ClusterLabelOption[]> {
  const multiClusterStore = useMultiClusterStore();
  if (multiClusterStore.clusters === null) {
    await multiClusterStore.fetchClusters();
  }
  const clusters: Cluster[] = orderBy(
    multiClusterStore.clusters,
    [(c: Cluster) => c.metadata?.name],
    ['asc'],
  );

  const labels = sortAndDedupClusterLabels(clusters);
  const clusterNames: ClusterLabelOption[] = clusters
    .filter((c): c is Cluster & { metadata: { name: string } } => !!c.metadata?.name)
    .map((c) => ({
      value: c.metadata.name,
      label: `Cluster - ${c.metadata.name}`,
      id: c.metadata.name,
    }));
  const clusterLabels: ClusterLabelOption[] = labels.map((label) => ({
    value: `${label[0]}=${label[1]}`,
    label: `${label[0]}:${label[1]}`,
    id: `${label[0]}=${label[1]}`,
  }));
  return clusterNames.concat(clusterLabels);
}

export const clusterState = {
  SUCCESS: 'success',
  FAILED: 'failed',
  IN_PROGRESS: 'inProgress',
} as const;

/**
 * Given the bootstrap conditions for a cluster in a bootstrap object
 * this returns a clusterState value - SUCCESS, FAILED, or IN_PROGRESS.
 * @param condition - array of bootstrap conditions
 */
export function bootstrapClusterState(
  condition: BootstrapCondition[] | undefined,
): (typeof clusterState)[keyof typeof clusterState] {
  const bootstrappedCondition = condition?.find((cond) => cond.type === 'Bootstrapped');
  const progressingCondition = condition?.find((cond) => cond.type === 'Progressing');
  const bootstrappedTrue = bootstrappedCondition?.status === 'True';
  const bootstrappedFalse = bootstrappedCondition?.status === 'False';
  const bootstrappedUnknown = bootstrappedCondition?.status === 'Unknown';
  const progressingTrue = progressingCondition?.status === 'True';
  const progressingFalse = progressingCondition?.status === 'False';

  if (bootstrappedTrue && progressingTrue) return clusterState.SUCCESS;
  if (bootstrappedFalse && progressingFalse) return clusterState.FAILED;
  if (bootstrappedUnknown) return clusterState.IN_PROGRESS;
  if (bootstrappedFalse && progressingTrue) return clusterState.IN_PROGRESS;
  return clusterState.IN_PROGRESS;
}
